'use client';

import { useState, useEffect } from 'react';
import { useRouter } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import { usePremium } from '@/contexts/PremiumContext';
import { elements, Element } from '@/data/elements';
import { BarChart3, Crown, ChevronLeft, ChevronRight, RotateCcw, Home, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslations } from 'next-intl';

export default function RevisionPage() {
  const { isPremium } = usePremium();
  const router = useRouter();
  const t = useTranslations('Revision');

  const [started, setStarted] = useState(false);
  const [currentCard, setCurrentCard] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [flashcards, setFlashcards] = useState<Element[]>([]);
  const [mastered, setMastered] = useState<Set<number>>(new Set());
  const [needsReview, setNeedsReview] = useState<Set<number>>(new Set());
  const [category, setCategory] = useState<string>('all');

  useEffect(() => {
    if (!isPremium) {
      toast.error(t('errorPremiumOnly'));
      router.push('/premium');
    }
  }, [isPremium, router, t]);

  const startRevision = () => {
    let filteredElements = [...elements];
    if (category !== 'all') {
      filteredElements = elements.filter(el => el.category === category);
    }
    const shuffled = filteredElements.sort(() => Math.random() - 0.5);
    setFlashcards(shuffled);
    setCurrentCard(0);
    setIsFlipped(false);
    setMastered(new Set());
    setNeedsReview(new Set());
    setStarted(true);
  };

  const handleFlip = () => setIsFlipped(!isFlipped);

  const handleMastered = () => {
    const newMastered = new Set(mastered);
    newMastered.add(flashcards[currentCard].atomicNumber);
    setMastered(newMastered);
    toast.success(t('toastMastered'));
    handleNext();
  };

  const handleNeedsReview = () => {
    const newNeedsReview = new Set(needsReview);
    newNeedsReview.add(flashcards[currentCard].atomicNumber);
    setNeedsReview(newNeedsReview);
    handleNext();
  };

  const handleNext = () => {
    if (currentCard < flashcards.length - 1) {
      setCurrentCard(currentCard + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
      setIsFlipped(false);
    }
  };

  const resetRevision = () => {
    setStarted(false);
    setCurrentCard(0);
    setIsFlipped(false);
    setMastered(new Set());
    setNeedsReview(new Set());
  };

  if (!isPremium) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <Crown className="w-24 h-24 text-[#FFD700] mx-auto mb-6" />
        <h1 className="text-4xl font-bold text-white mb-4">{t('premiumTitle')}</h1>
        <p className="text-[#a8b2d1] text-xl mb-8">{t('premiumDesc')}</p>
        <Button
          onClick={() => router.push('/premium')}
          className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0a192f] hover:opacity-90 font-bold"
        >
          <Crown className="w-5 h-5 mr-2" />
          {t('upgradeToPremium')}
        </Button>
      </div>
    );
  }

  if (!started) {
    const categories = [
      { value: 'all', labelKey: 'allElements' as const, count: 118 },
      { value: 'noble_gas', labelKey: 'nobleGases' as const, count: elements.filter(e => e.category === 'noble_gas').length },
      { value: 'alkali_metal', labelKey: 'alkaliMetals' as const, count: elements.filter(e => e.category === 'alkali_metal').length },
      { value: 'halogen', labelKey: 'halogens' as const, count: elements.filter(e => e.category === 'halogen').length },
      { value: 'transition_metal', labelKey: 'transitionMetals' as const, count: elements.filter(e => e.category === 'transition_metal').length },
      { value: 'nonmetal', labelKey: 'nonmetals' as const, count: elements.filter(e => e.category === 'nonmetal').length },
    ];

    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <div className="text-center mb-12">
          <BarChart3 className="w-20 h-20 text-[#64ffda] mx-auto mb-6" />
          <h1 className="text-5xl font-bold text-white mb-4">{t('title')}</h1>
          <p className="text-xl text-[#a8b2d1]">{t('subtitle')}</p>
        </div>

        <div className="bg-[#112240] border-2 border-[#233554] rounded-xl p-8">
          <h2 className="text-2xl font-bold text-[#64ffda] mb-6">{t('chooseCategory')}</h2>

          <div className="grid gap-3 mb-8">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategory(cat.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  category === cat.value
                    ? 'border-[#64ffda] bg-[#64ffda]/10'
                    : 'border-[#233554] hover:border-[#64ffda]/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{t(cat.labelKey)}</span>
                  <span className="text-sm text-[#a8b2d1]">{cat.count} {t('cards')}</span>
                </div>
              </button>
            ))}
          </div>

          <Button
            onClick={startRevision}
            className="w-full bg-gradient-to-r from-[#64ffda] to-[#82aaff] text-[#0a192f] hover:opacity-90 font-bold text-lg py-6"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            {t('startRevision')}
          </Button>
        </div>
      </div>
    );
  }

  if (currentCard >= flashcards.length) {
    const masteredCount = mastered.size;
    const reviewCount = needsReview.size;
    const skippedCount = flashcards.length - masteredCount - reviewCount;

    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <div className="text-center mb-12">
          <BarChart3 className="w-24 h-24 text-[#64ffda] mx-auto mb-6" />
          <h1 className="text-5xl font-bold text-white mb-4">{t('sessionDone')}</h1>
          <p className="text-xl text-[#a8b2d1]">{t('yourStats')}</p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-green-500/20 border-2 border-green-500 rounded-xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <div className="text-3xl font-bold text-white mb-1">{masteredCount}</div>
            <div className="text-sm text-green-400">{t('mastered')}</div>
          </div>

          <div className="bg-yellow-500/20 border-2 border-yellow-500 rounded-xl p-6 text-center">
            <RefreshCw className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
            <div className="text-3xl font-bold text-white mb-1">{reviewCount}</div>
            <div className="text-sm text-yellow-400">{t('toReview')}</div>
          </div>

          <div className="bg-gray-500/20 border-2 border-gray-500 rounded-xl p-6 text-center">
            <XCircle className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <div className="text-3xl font-bold text-white mb-1">{skippedCount}</div>
            <div className="text-sm text-gray-400">{t('skipped')}</div>
          </div>
        </div>

        <div className="bg-[#112240] border-2 border-[#233554] rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-[#64ffda] mb-4">{t('progressTitle')}</h3>
          <div className="w-full bg-[#233554] rounded-full h-4 mb-2">
            <div
              className="bg-gradient-to-r from-green-500 to-green-400 h-4 rounded-full"
              style={{ width: `${(masteredCount / flashcards.length) * 100}%` }}
            />
          </div>
          <p className="text-sm text-[#a8b2d1] text-center">
            {t('progressText', { percent: Math.round((masteredCount / flashcards.length) * 100) })}
          </p>
        </div>

        <div className="flex gap-4">
          <Button
            onClick={resetRevision}
            className="flex-1 bg-gradient-to-r from-[#64ffda] to-[#82aaff] text-[#0a192f] hover:opacity-90"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('newSession')}
          </Button>
          <Button
            onClick={() => router.push('/')}
            variant="outline"
            className="flex-1 border-[#64ffda] text-[#64ffda] hover:bg-[#64ffda]/10"
          >
            <Home className="w-4 h-4 mr-2" />
            {t('home')}
          </Button>
        </div>
      </div>
    );
  }

  const element = flashcards[currentCard];
  const progress = Math.round(((currentCard + 1) / flashcards.length) * 100);

  return (
    <div className="container mx-auto px-4 py-16 max-w-2xl">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[#a8b2d1]">
            {t('cardOf', { current: currentCard + 1, total: flashcards.length })}
          </span>
          <div className="flex gap-4 text-sm">
            <span className="text-green-400">✓ {mastered.size}</span>
            <span className="text-yellow-400">↻ {needsReview.size}</span>
          </div>
        </div>
        <div className="w-full bg-[#233554] rounded-full h-3">
          <div
            className="bg-gradient-to-r from-[#64ffda] to-[#82aaff] h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div
        className="relative w-full h-[400px] cursor-pointer mb-8 perspective-1000"
        onClick={handleFlip}
      >
        <div
          className={`absolute w-full h-full transition-all duration-500 transform-style-3d ${
            isFlipped ? 'rotate-y-180' : ''
          }`}
        >
          {/* Front */}
          <div
            className={`absolute w-full h-full bg-gradient-to-br from-[#112240] to-[#0a192f] border-4 rounded-2xl p-8 flex flex-col items-center justify-center backface-hidden ${
              isFlipped ? 'invisible' : 'visible'
            }`}
            style={{ borderColor: element.color }}
          >
            <div className="text-center">
              <div
                className="text-8xl font-bold mb-4"
                style={{ color: element.color, textShadow: `0 0 40px ${element.color}80` }}
              >
                {element.symbol}
              </div>
              <div className="text-3xl text-white font-bold mb-2">
                {t('number', { num: element.atomicNumber })}
              </div>
              <p className="text-[#a8b2d1] text-sm">{t('clickToReveal')}</p>
            </div>
          </div>

          {/* Back */}
          <div
            className={`absolute w-full h-full bg-gradient-to-br from-[#0a192f] to-[#112240] border-4 rounded-2xl p-8 rotate-y-180 backface-hidden overflow-y-auto ${
              isFlipped ? 'visible' : 'invisible'
            }`}
            style={{ borderColor: element.color }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">{element.name}</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">{t('symbolLabel')}</span>
                <span className="text-white font-bold text-2xl" style={{ color: element.color }}>
                  {element.symbol}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">{t('atomicNumberLabel')}</span>
                <span className="text-white font-semibold">{element.atomicNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">{t('atomicMassLabel')}</span>
                <span className="text-white font-semibold">{element.atomicWeight.toFixed(3)} u</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">{t('categoryLabel')}</span>
                <span className="text-white font-semibold">{element.category.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">{t('stateLabel')}</span>
                <span className="text-white font-semibold capitalize">{element.state}</span>
              </div>
              <div className="pt-3 border-t border-[#233554]">
                <p className="text-white font-mono text-xs bg-black/40 p-2 rounded">
                  {element.electronConfiguration}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Button
          onClick={handleNeedsReview}
          variant="outline"
          className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('needsReview')}
        </Button>
        <Button
          onClick={handleMastered}
          className="bg-green-500 text-white hover:bg-green-600"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          {t('masteredCard')}
        </Button>
      </div>

      <div className="flex gap-4">
        <Button
          onClick={handlePrevious}
          disabled={currentCard === 0}
          variant="outline"
          className="flex-1 border-[#233554] text-[#a8b2d1] hover:bg-[#233554]"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          {t('previous')}
        </Button>
        <Button
          onClick={handleNext}
          disabled={currentCard >= flashcards.length - 1}
          variant="outline"
          className="flex-1 border-[#233554] text-[#a8b2d1] hover:bg-[#233554]"
        >
          {t('next')}
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
